import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { DocumentUpload, Edit2, Eye, EyeSlash, Trash } from 'iconsax-react'

// import LockIcon from '../../icons/LockIcon'
import MoreHorizontalIcon from '../../icons/MoreHorizontalIcon'
import MoreHorizontalSmallIcon from '../../icons/MoreHorizontalSmallIcon'

import ButtonMenu from '../ButtonMenu2'
import EditFolderModal from '../ChatModals/EditFolderModal'

import { toTitleCase } from '@SonarCX/utilities'
import {
  selectArticles,
  deleteArticle,
  togglePublish,
  startCreateInFolder
} from '@SonarCX/slices/article'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'

// const FOLDER_PRIVATE = 'Set to Private'
const FOLDER_EDIT = 'Edit Folder Detail'
const FOLDER_DELETE = 'Delete Folder'
const FOLDER_PUBLISH = 'Publish Folder'
const FOLDER_UNPUBLISH = 'Unpublish Folder'
const FOLDER_CREATE_DOC = 'Create Article'

const DOCUMENT_PUBLISH = 'Publish on Website'
const DOCUMENT_UNPUBLISH = 'Unpublish'
// const DOCUMENT_DRAFT = 'Draft'
const DOCUMENT_DELETE = 'Delete Article'

const ArticleOptionsMenu = ({
  smallIcon = false,
  articleId,
  disabled = false,
  className = undefined
}) => {
  const dispatch = useAppDispatch()

  const { root, articles, selected } = useAppSelector(selectArticles)

  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const [editingFolder, setEditingFolder] = React.useState(false)

  const article = React.useMemo(() => {
    if (articleId in articles) {
      return articles[articleId]
    }

    const rootArticle = root.items.find(item => item.id === articleId)
    if (rootArticle) {
      return rootArticle
    }

    let otherArticle = null

    Object.values(articles).forEach(article => {
      if (Array.isArray(article.items)) {
        const thisArticle = article.items.find(item => item.id === articleId)
        if (thisArticle && !otherArticle) {
          otherArticle = thisArticle
        }
      }
    })

    return otherArticle ?? {}
  }, [articles, articleId, root])

  const isFolder = React.useMemo(
    () => article.type === 'folder',
    [article.type]
  )

  const menuName = React.useMemo(
    () => (isFolder ? 'Folder' : 'Article'),
    [isFolder]
  )

  const FOLDER_OPTIONS = React.useMemo(() => {
    const deleteDisabled = article.total_items > 0

    const options = [
      // { name: FOLDER_PRIVATE, Icon: LockIcon },
      { name: FOLDER_EDIT, Icon: Edit2 },
      { name: FOLDER_DELETE, Icon: Trash, disabled: deleteDisabled },
      { name: FOLDER_CREATE_DOC, Icon: DocumentUpload }
    ]

    if (article.published_at) {
      options.push({ name: FOLDER_UNPUBLISH, Icon: EyeSlash })
    } else {
      options.push({ name: FOLDER_PUBLISH, Icon: Eye })
    }

    return options
  }, [article.total_items, article.published_at])

  const DOCUMENT_OPTIONS = React.useMemo(() => {
    const options = [
      // { name: DOCUMENT_DRAFT, Icon: Edit2 },
      { name: DOCUMENT_DELETE, Icon: Trash }
    ]

    if (article.published_at) {
      options.unshift({ name: DOCUMENT_UNPUBLISH, Icon: EyeSlash })
    } else {
      options.unshift({ name: DOCUMENT_PUBLISH, Icon: Eye })
    }

    return options
  }, [article.published_at])

  const options = React.useMemo(() => {
    if (isFolder) {
      return FOLDER_OPTIONS
    }

    return DOCUMENT_OPTIONS
  }, [isFolder, FOLDER_OPTIONS, DOCUMENT_OPTIONS])

  const handleDelete = React.useCallback(() => {
    const type = article.type === 'article' ? 'document' : article.type
    setReason(`Delete ${toTitleCase(type)}`)
    setSubtext(`Are you sure you want to delete this ${type}?`)
    setV2(true)
    setAction(() => {
      dispatch(
        deleteArticle({
          id: articleId,
          type: article.type,
          redirect: selected === articleId ? null : undefined
        })
      )
    })
  }, [
    dispatch,
    articleId,
    setReason,
    setSubtext,
    setV2,
    setAction,
    article.type,
    selected
  ])

  const handleCreateDocumentInFolder = React.useCallback(() => {
    dispatch(startCreateInFolder(article.id))
  }, [dispatch, article])

  const handlePublish = React.useCallback(
    name => {
      const willPublish = [FOLDER_PUBLISH, DOCUMENT_PUBLISH].includes(name)
      setTitle('ARE YOU SURE?')
      setReason(
        willPublish
          ? 'Publishing this on your website will make this content visible to the public.'
          : 'Unpublishing will remove this content from your website.'
      )
      setSubtext('Are you sure?')
      setV2(true)
      setAction(() => {
        dispatch(togglePublish(article))
      })
    },
    [setTitle, setReason, setSubtext, setV2, setAction, dispatch, article]
  )

  const handleSelect = React.useCallback(
    name => {
      if ([FOLDER_DELETE, DOCUMENT_DELETE].includes(name)) {
        handleDelete()
        return
      }

      if (name === FOLDER_EDIT) {
        setEditingFolder(true)
        return
      }

      if (name === FOLDER_CREATE_DOC) {
        handleCreateDocumentInFolder()
        return
      }

      if (
        [
          FOLDER_PUBLISH,
          FOLDER_UNPUBLISH,
          DOCUMENT_PUBLISH,
          DOCUMENT_UNPUBLISH
        ].includes(name)
      ) {
        handlePublish(name)
      }
    },
    [handleDelete, handleCreateDocumentInFolder, handlePublish]
  )

  const icon = React.useMemo(
    () => (smallIcon ? <MoreHorizontalSmallIcon /> : <MoreHorizontalIcon />),
    [smallIcon]
  )

  return (
    <>
      <ButtonMenu
        title={menuName}
        options={options}
        onSelect={handleSelect}
        disabled={disabled}
        className={className}
        smallIcons
      >
        {icon}
      </ButtonMenu>

      {editingFolder && (
        <EditFolderModal
          article={article}
          onClose={() => setEditingFolder(false)}
        />
      )}
    </>
  )
}

export default ArticleOptionsMenu
